import { BearingEnvelope } from '@paragon/contract-library';
import { LineSegment2D, Point2D, point2DEquals, Vector2D } from '@paragon/geometry-library';
import { getEnvelopeSegment } from '@paragon/layout';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { translateBearingEnvelope } from '../../../library/layout-geometry/bearing-envelopes';
import {
  moveSoftLockStateEquals,
  roundPoint2D,
  snapPointPositionsDuringInteractiveMoveEdit,
  snapSegmentLengthDuringInteractiveEdit,
} from '../../../library/layout-snapping/interactive-snapping';
import { getSelectedItemIdentifiers } from '../../../library/selection/selectors';
import { isBearingEnvelopeSelectableType } from '../../../library/selection/types';
import { getDistanceSnap } from '../../../three/hooks/useMousePositionWithBasicInsertionSnapping';
import {
  getActiveEditHasMoved,
  getActiveInteractiveEdit,
  getActiveMoveEditBaseShapeVertices,
  getInteractiveEditAxisSnapTargets,
  isInteractiveEditActive,
  shouldInteractiveEditingBeDisabled,
} from '../../selectors/interactiveEditingSelectors';
import { getAllBearingEnvelopesDictionary } from '../../selectors/layoutDataSelectors';
import { getActualInteractiveEditSnappingSettings } from '../../selectors/snappingSettingsSelectors';
import { EnvelopeEndpoint, interactiveEditingSlice } from '../../slices/interactiveEditingSlice';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';
import { BearingEnvelopeEditInstructions, editBearingEnvelopes } from './bearingEnvelopeActions';

/**
 * Determines which end ('left' or 'right') of the given envelope the given point is closest to.
 */
function determineClosestEnd(point: Point2D, envelope: BearingEnvelope): EnvelopeEndpoint {
  const leftPoint = Point2D.fromObject(envelope.leftPoint);
  const rightPoint = Point2D.fromObject(envelope.rightPoint);

  const distToLeft = point.distanceTo(leftPoint);
  const distToRight = point.distanceTo(rightPoint);

  return distToLeft < distToRight ? 'left' : 'right';
}

/**
 * Starts an interactive edit on a bearing envelope based on the edge type.
 *
 * @param guid - The GUID of the bearing envelope to edit
 * @param edgeType - Whether the interaction started on the 'end' of the envelope (a drag to resize) or its 'interior'
 *                     (a drag to move).
 * @param edgeCenter - The center point of the edge (used to determine which endpoint for 'end' edges)
 * @param startPosition - The mouse position where the drag started
 */
export function startBearingEnvelopeEdit(
  guid: string,
  edgeType: 'end' | 'interior',
  edgeCenter: Point2D,
  startPosition: Point2D,
): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    if (shouldInteractiveEditingBeDisabled(state)) return;

    const envelopes = getAllBearingEnvelopesDictionary(state);
    const envelope = envelopes[guid];
    if (!envelope) return;

    const selectedBearingGuids = getSelectedItemIdentifiers(state)
      .filter((item) => isBearingEnvelopeSelectableType(item.type))
      .map((item) => item.guid);

    if (!selectedBearingGuids.includes(guid)) return;

    switch (edgeType) {
      case 'end': {
        const endpoint = determineClosestEnd(edgeCenter, envelope);
        dispatch(
          interactiveEditingSlice.actions.__UNSAFE_startLengthEdit({
            guid,
            endpoint,
            baseEnvelope: envelope,
          }),
        );
        break;
      }

      case 'interior': {
        const editedEnvelopes = selectedBearingGuids
          .map((g) => {
            const baseEnvelope = envelopes[g];
            return baseEnvelope == null ? null : { guid: g, baseEnvelope };
          })
          .filter(notNullish);

        dispatch(
          interactiveEditingSlice.actions.__UNSAFE_startMoveEdit({
            draggedGuid: guid,
            editedEnvelopes,
            dragStartPoint: startPosition.toObject(),
          }),
        );
        break;
      }
    }
  };
}

/**
 * Updates the preview for the active edit based on the current mouse position.
 */
export function updateBearingEnvelopeEditPreview(mousePosition: Point2D): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const activeEdit = getActiveInteractiveEdit(state);
    if (!activeEdit) return;
    const interactiveEditSnappingSettings = getActualInteractiveEditSnappingSettings(state);

    switch (activeEdit.type) {
      case 'bearingEnvelopeLength': {
        // Project mouse position onto the defining line (constrained movement)
        const definingSegment = getEnvelopeSegment(activeEdit.baseEnvelope);
        if (!definingSegment) return;

        const definingLine = definingSegment.toLine();
        const projectedPoint = mousePosition.projectOntoLine(definingLine);

        // Snap the projected point to the nearest increment from the fixed endpoint
        const fixedPoint =
          activeEdit.endpoint === 'left'
            ? Point2D.fromObject(activeEdit.baseEnvelope.rightPoint)
            : Point2D.fromObject(activeEdit.baseEnvelope.leftPoint);

        if (projectedPoint.equals(fixedPoint)) return;
        const segment = new LineSegment2D(fixedPoint, projectedPoint);

        const snappingLevel = state.application.snappingLevel;
        const snapIncrement = getDistanceSnap(snappingLevel);

        const axisSnapTargets = getInteractiveEditAxisSnapTargets(state);

        const snapResult = snapSegmentLengthDuringInteractiveEdit(
          segment,
          snapIncrement,
          axisSnapTargets,
          activeEdit.lengthSoftLockState,
          interactiveEditSnappingSettings,
        );

        // Only update if the point or soft-lock state changed
        const currentPatch = activeEdit.currentPatch;
        const softLockChanged = activeEdit.lengthSoftLockState !== snapResult.softLockState;
        if (activeEdit.endpoint === 'left') {
          if (!currentPatch.leftPoint || !point2DEquals(currentPatch.leftPoint, snapResult.point) || softLockChanged) {
            dispatch(
              interactiveEditingSlice.actions.updateLengthEditPatch({
                leftPoint: snapResult.point.toObject(),
                lengthSoftLockState: snapResult.softLockState,
              }),
            );
          }
        } else {
          if (
            !currentPatch.rightPoint ||
            !point2DEquals(currentPatch.rightPoint, snapResult.point) ||
            softLockChanged
          ) {
            dispatch(
              interactiveEditingSlice.actions.updateLengthEditPatch({
                rightPoint: snapResult.point.toObject(),
                lengthSoftLockState: snapResult.softLockState,
              }),
            );
          }
        }
        break;
      }

      case 'bearingEnvelopeMove': {
        const dragStart = Point2D.fromObject(activeEdit.dragStartPoint);
        const mouseDelta = Vector2D.fromPointAndPoint(dragStart, mousePosition);

        const axisSnapTargets = getInteractiveEditAxisSnapTargets(state);
        const baseShapeVertices = getActiveMoveEditBaseShapeVertices(state);

        const snapResult = snapPointPositionsDuringInteractiveMoveEdit(
          baseShapeVertices,
          mouseDelta,
          axisSnapTargets,
          activeEdit.moveSoftLockState,
          interactiveEditSnappingSettings,
        );

        const draggedEnvelope = activeEdit.editedEnvelopes.find((entry) => entry.guid === activeEdit.draggedGuid);
        if (!draggedEnvelope) return;

        const draggedEnvelopePoint = roundPoint2D(
          Point2D.fromObject(draggedEnvelope.baseEnvelope.leftPoint).translate(snapResult.delta),
        );
        const draggedEnvelopeDelta = Vector2D.fromPointAndPoint(
          draggedEnvelope.baseEnvelope.leftPoint,
          draggedEnvelopePoint,
        );

        if (
          !point2DEquals(activeEdit.currentDelta, draggedEnvelopeDelta.imageOfOrigin) ||
          !moveSoftLockStateEquals(activeEdit.moveSoftLockState, snapResult.softLockState)
        ) {
          dispatch(
            interactiveEditingSlice.actions.updateMoveEditDelta({
              currentDelta: draggedEnvelopeDelta.imageOfOrigin.toObject(),
              moveSoftLockState: snapResult.softLockState,
            }),
          );
        }
        break;
      }

      default:
        assertExhaustiveSwitch(activeEdit);
    }
  };
}

/**
 * Commits the active edit by applying the patch to the bearing envelope via editBearingEnvelopes,
 * then clears the edit state.
 */
export function commitActiveEdit(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const activeEdit = getActiveInteractiveEdit(getState());
    if (activeEdit == null) return;

    const edits: BearingEnvelopeEditInstructions[] = [];

    const envelopeActuallyMoved = getActiveEditHasMoved(getState());

    switch (activeEdit.type) {
      case 'bearingEnvelopeLength': {
        if (envelopeActuallyMoved) {
          edits.push({ guid: activeEdit.guid, editedProperties: activeEdit.currentPatch });
        }

        break;
      }

      case 'bearingEnvelopeMove': {
        if (envelopeActuallyMoved) {
          const delta = Vector2D.fromOriginTo(activeEdit.currentDelta);

          for (const entry of activeEdit.editedEnvelopes) {
            const translatedEnvelope = translateBearingEnvelope(entry.baseEnvelope, delta);

            edits.push({
              guid: entry.guid,
              editedProperties: {
                leftPoint: translatedEnvelope.leftPoint,
                rightPoint: translatedEnvelope.rightPoint,
              },
            });
          }
        }
        break;
      }

      default:
        assertExhaustiveSwitch(activeEdit);
    }

    dispatch(interactiveEditingSlice.actions.clearActiveEdit());

    if (edits.length > 0) {
      await dispatch(editBearingEnvelopes(edits));
    }
  };
}

/**
 * Cancels the active edit if one is in progress, reverting to the original state.
 */
export function cancelActiveEditIfActive(): ThunkActionVoid {
  return (dispatch, getState) => {
    if (isInteractiveEditActive(getState())) {
      dispatch(interactiveEditingSlice.actions.clearActiveEdit());
    }
  };
}
